import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuth } from 'firebase/auth';

interface NumberOption {
    id: string;
    number: string;
    price: number;
    country: string;
    countryCode: string;
    countryPrefix: string;
    duration: number;
}

const EmptySimcard: React.FC = () => {
    const navigate = useNavigate();
    const [selectedCountry, setSelectedCountry] = useState('United States');
    const [searchResults, setSearchResults] = useState<NumberOption[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [purchasingOptionId, setPurchasingOptionId] = useState<string | null>(null);

    const dropdownRef = useRef<HTMLDivElement>(null);

    const formatPrice = (price: any): string => {
        const numPrice = parseFloat(Number(price).toFixed(2));
        return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
    };

    const countries = [
        {
            code: 'US',
            name: 'United States',
            prefix: '+1',
            flag: (
                <svg className="w-5 h-4 inline-block mr-2" viewBox="0 0 60 40">
                    <rect width="60" height="40" fill="#B22234" />
                    <rect width="60" height="3" y="3" fill="white" />
                    <rect width="60" height="3" y="9" fill="white" />
                    <rect width="60" height="3" y="15" fill="white" />
                    <rect width="60" height="3" y="21" fill="white" />
                    <rect width="60" height="3" y="27" fill="white" />
                    <rect width="60" height="3" y="33" fill="white" />
                    <rect width="24" height="21" fill="#3C3B6E" />
                </svg>
            )
        },
        {
            code: 'GB',
            name: 'United Kingdom',
            prefix: '+44',
            flag: (
                <svg className="w-5 h-4 inline-block mr-2" viewBox="0 0 60 40">
                    <rect width="60" height="40" fill="#012169" />
                    <path d="M0 0L60 40M60 0L0 40" stroke="white" strokeWidth="4" />
                    <path d="M0 0L60 40M60 0L0 40" stroke="#C8102E" strokeWidth="2" />
                    <path d="M30 0V40M0 20H60" stroke="white" strokeWidth="8" />
                    <path d="M30 0V40M0 20H60" stroke="#C8102E" strokeWidth="4" />
                </svg>
            )
        }
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleErrorModalClose = () => {
        setShowErrorModal(false);
        setErrorMessage('');
    };

    const handlePurchase = async (option: NumberOption) => {
        const currentUser = getAuth().currentUser;

        if (!currentUser) {
            setErrorMessage('You are not authenticated or your token is invalid');
            setShowErrorModal(true);
            return;
        }

        const uniqueOptionId = option.id;
        setPurchasingOptionId(uniqueOptionId);

        try {
            const idToken = await currentUser.getIdToken();

            let endpoint = 'https://buyemptysimcardusa-ezeznlhr5a-uc.a.run.app';
            let bodyData: any = undefined;

            if (selectedCountry === 'United Kingdom') {
                endpoint = 'https://buyemptysimcarduk-ezeznlhr5a-uc.a.run.app';
                let durationString = '';
                // if (option.duration === 1) durationString = 'oneDay';
                // else if (option.duration === 14) durationString = 'oneWeek';
                if (option.duration === 7) durationString = 'oneWeek';
                else if (option.duration === 30) durationString = 'thirtyDays';

                bodyData = { duration: durationString };
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'authorization': `${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: bodyData ? JSON.stringify(bodyData) : undefined
            });

            const data = await response.json();

            if (response.ok && data.success) {
                navigate('/history');
            } else {
                let errorMsg = 'An unknown error occurred';

                if (data.message === 'Unauthorized') {
                    errorMsg = 'You are not authenticated or your token is invalid';
                } else if (data.message === 'Invalid serviceId' || data.message === 'Invalid duration') {
                    errorMsg = 'Please refresh the page and try again';
                } else if (data.message === 'You cannot buy an empty simcard, because you have used Amazon Pay') {
                    errorMsg = 'Users that deposit with Amazon Pay cannot purchase empty SIM cards';
                } else if (data.message === 'Insufficient balance') {
                    errorMsg = 'You do not have enough balance to make the purchase';
                } else if (data.message === 'Internal Server Error') {
                    errorMsg = 'Please contact our customer support';
                } else if (data.message === 'Service unavailable') {
                    errorMsg = 'We ran out of SIM cards, try again later';
                }

                setErrorMessage(errorMsg);
                setShowErrorModal(true);
            }
        } catch (error) {
            setErrorMessage('Please contact our customer support');
            setShowErrorModal(true);
        } finally {
            setPurchasingOptionId(null);
        }
    };

    const handleSearch = async () => {
        setIsSearching(true);
        setHasSearched(false);

        try {
            let result: NumberOption[] = [];
            const selectedCountryData = countries.find(c => c.name === selectedCountry);
            const countryCode = selectedCountryData?.code || 'US';
            const countryPrefix = selectedCountryData?.prefix || '+1';

            if (selectedCountry === 'United Kingdom') {
                const docRef = doc(db, 'fees', 'emptySimcardCatalogUK');
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    throw new Error('not-found');
                }

                const data = docSnap.data();
                // if (!data.oneDay || !data.oneWeek || !data.thirtyDays) {
                if (!data.oneWeek || !data.thirtyDays) {
                    throw new Error('Price data not available');
                }

                result = [
                    // {
                    //     id: 'empty-simcard-uk-1',
                    //     number: `${countryPrefix}-XXXXXX`,
                    //     price: Number(data.oneDay),
                    //     country: selectedCountry,
                    //     countryCode: countryCode,
                    //     countryPrefix: countryPrefix,
                    //     duration: 1
                    // },
                    // {
                    //     id: 'empty-simcard-uk-14',
                    //     number: `${countryPrefix}-XXXXXX`,
                    //     price: Number(data.oneWeek),
                    //     country: selectedCountry,
                    //     countryCode: countryCode,
                    //     countryPrefix: countryPrefix,
                    //     duration: 14
                    // },
                    {
                        id: 'empty-simcard-uk-7',
                        number: `${countryPrefix}-XXXXXX`,
                        price: Number(data.oneWeek),
                        country: selectedCountry,
                        countryCode: countryCode,
                        countryPrefix: countryPrefix,
                        duration: 7
                    },
                    {
                        id: 'empty-simcard-uk-30',
                        number: `${countryPrefix}-XXXXXX`,
                        price: Number(data.thirtyDays),
                        country: selectedCountry,
                        countryCode: countryCode,
                        countryPrefix: countryPrefix,
                        duration: 30
                    }
                ];
            } else {
                const docRef = doc(db, 'fees', 'emptySimcardCatalog');
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    throw new Error('not-found');
                }

                const data = docSnap.data();
                const thirtyDaysPrice = data.thirtyDays;

                if (!thirtyDaysPrice) {
                    throw new Error('Price data not available');
                }

                result = [
                    {
                        id: 'empty-simcard-1',
                        number: `${countryPrefix}-XXXXXX`,
                        price: Number(thirtyDaysPrice),
                        country: selectedCountry,
                        countryCode: countryCode,
                        countryPrefix: countryPrefix,
                        duration: 30
                    }
                ];
            }

            setSearchResults(result);
            setHasSearched(true);
        } catch (error) {

            setSearchResults([]);
            setHasSearched(false);

            let userErrorMessage = 'An error occurred while searching for numbers, please try again';

            if (error instanceof Error) {
                if (error.message.includes('permission-denied')) {
                    userErrorMessage = 'You do not have permission to access the number search';
                } else if (error.message.includes('unavailable')) {
                    userErrorMessage = 'Number search is temporarily unavailable, please try again later';
                } else if (error.message.includes('network')) {
                    userErrorMessage = 'Network connection error, please check your internet connection';
                } else if (error.message.includes('not-found')) {
                    userErrorMessage = 'Number options not found, please contact customer support';
                } else if (error.message.includes('quota-exceeded')) {
                    userErrorMessage = 'Service quota exceeded, please try again later';
                }
            }

            setErrorMessage(userErrorMessage);
            setShowErrorModal(true);
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="rounded-3xl shadow-2xl border border-slate-700/50 p-6 relative overflow-hidden">
                <div className="relative z-10">
                    <div className="flex items-center space-x-4">
                        <div>
                            <h1 className="text-left text-2xl font-bold bg-gradient-to-r from-white via-emerald-100 to-green-100 bg-clip-text text-transparent">
                                Empty SIM cards
                            </h1>
                            <p className="text-slate-300 text-md text-left">30-day validity numbers for multiple verifications</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Information Section - Always on top */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-3xl p-4 mb-6">
                <div className="flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-blue-300 text-sm font-semibold mb-3">Important information about these numbers:</p>
                        <ul className="text-blue-200 text-xs mt-1 space-y-2 text-left">
                            <li>• They can be used to verify more than 1 service</li>
                            <li>• For USA: they are valid for 30 days, for UK: they are valid for 7 or 30 days</li>
                            <li>• Their duration can only be extended before they expire</li>
                            <li>• After purchased, some can be cancelled and some can't</li>
                            <li>• Users that deposit through Amazon Pay can't purchase them</li>
                            <li>• If you want to reuse a number for 9-10 minutes or choose area code/carrier, go to <Link to="/short" className="text-blue-400 hover:text-blue-300 underline font-semibold">Short Numbers</Link></li>
                            <li>• If you want to verify 1 service more than once for a longer period, go to <Link to="/middle" className="text-blue-400 hover:text-blue-300 underline font-semibold">Middle</Link> or <Link to="/long" className="text-blue-400 hover:text-blue-300 underline font-semibold">Long</Link> Numbers</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* UK Empty SIM Cards Announcement */}
            <div className="bg-gradient-to-r from-emerald-500/10 via-green-500/5 to-emerald-500/10 border border-emerald-500/30 rounded-2xl px-4 py-3 mb-6">
                <p className="text-center text-sm">
                    <span className="text-emerald-300 font-bold">NEW</span>
                    <span className="text-slate-300 mx-2">—</span>
                    <span className="text-slate-200">Now you can purchase Empty SIM Cards from <span className="text-emerald-400 font-semibold">United Kingdom</span>, scroll down the country selector to see it!</span>
                </p>
            </div>

            {!hasSearched ? (
                <>
                    {/* Main Content Section */}
                    <div className={`rounded-3xl shadow-2xl border border-slate-700/50 relative ${isCountryDropdownOpen ? 'overflow-visible' : 'overflow-hidden'}`}>
                        <div className="p-6">
                            <div className="relative z-10 mx-auto">
                                {/* Search Header */}
                                <div className="text-left mb-9">
                                    <h1 className="text-2xl font-bold bg-gradient-to-r from-white via-emerald-100 to-green-100 bg-clip-text text-transparent mb-2">
                                        Search For Numbers
                                    </h1>
                                    <p className="text-slate-300 text-md">Find your multi-service SIM card</p>
                                </div>

                                {/* Search Form */}
                                <div className="space-y-9">
                                    {/* Form Elements Container */}
                                    <div className="space-y-6">
                                        {/* Country Selection and Search Button Row */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                                            {/* Country Selection */}
                                            <div className="space-y-3 relative z-20">
                                                <label className="block text-sm font-semibold text-emerald-300 uppercase tracking-wider text-center">
                                                    Select Country
                                                </label>
                                                <div className="relative group" ref={dropdownRef}>
                                                    <div
                                                        onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                                                        className="w-full pl-12 pr-10 py-3 bg-slate-800/50 border-2 border-slate-600/50 rounded-2xl text-white cursor-pointer text-sm shadow-inner hover:border-slate-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500/50 transition-all duration-300 flex items-center justify-between"
                                                    >
                                                        <div className="flex items-center">
                                                            <div className="absolute left-4">
                                                                {countries.find(c => c.name === selectedCountry)?.flag}
                                                            </div>
                                                            <span>{selectedCountry}</span>
                                                        </div>
                                                    </div>

                                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                        <svg className={`h-6 w-6 text-emerald-400 transition-transform duration-300 ${isCountryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </div>

                                                    {/* Custom Dropdown Options */}
                                                    {isCountryDropdownOpen && (
                                                        <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-slate-600/50 rounded-2xl shadow-xl z-50 max-h-60 overflow-y-auto">
                                                            {countries.map((country) => (
                                                                <div
                                                                    key={country.code}
                                                                    onClick={() => {
                                                                        setSelectedCountry(country.name);
                                                                        setIsCountryDropdownOpen(false);
                                                                    }}
                                                                    className="flex items-center px-4 py-3 hover:bg-slate-700/50 cursor-pointer transition-colors duration-200 first:rounded-t-2xl last:rounded-b-2xl"
                                                                >
                                                                    <div className="mr-1">
                                                                        {country.flag}
                                                                    </div>
                                                                    <span className="text-white">{country.name}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Search Button */}
                                            <div className="space-y-3 flex flex-col justify-end">
                                                <div className="hidden md:block text-sm font-semibold text-transparent uppercase tracking-wider">
                                                    Search
                                                </div>
                                                <button
                                                    onClick={handleSearch}
                                                    disabled={isSearching}
                                                    className="group px-8 py-3 bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 hover:from-emerald-500 hover:via-green-500 hover:to-teal-500 text-white font-bold text-md rounded-2xl transition-all duration-300 shadow-2xl hover:shadow-emerald-500/25 hover:scale-105 border border-emerald-500/30 hover:border-emerald-400/50 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 min-w-[150px]"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-green-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                                    <div className="relative z-10 flex items-center justify-center">
                                                        {isSearching ? (
                                                            <svg className="w-6 h-6 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                            </svg>
                                                        ) : (
                                                            <span className="group-hover:tracking-wide transition-all duration-300">
                                                                <span>Search numbers</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {/* Results Section */}
                    <div className="rounded-3xl shadow-2xl border border-slate-700/50 relative">
                        <div className="p-6">
                            <div className="relative z-10">
                                {/* Back Button */}
                                <div className="mb-5">
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="group flex items-center space-x-3 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-600/50 hover:border-slate-500/50 rounded-xl transition-all duration-300 backdrop-blur-sm"
                                    >
                                        <svg className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                        </svg>
                                        <span className="text-slate-400 group-hover:text-white font-medium transition-colors duration-300">
                                            Back to Search
                                        </span>
                                    </button>
                                </div>

                                {/* Results Header */}
                                <div className="text-left mb-7">
                                    <h1 className="text-2xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                                        Number options
                                    </h1>
                                    <p className="text-slate-300 text-md">
                                        {searchResults.length > 0
                                            ? (
                                                <span className="flex items-center flex-wrap">
                                                    Found {searchResults.length} {searchResults.length === 1 ? 'number' : 'numbers'} from {selectedCountry}
                                                    <span className="ml-2 hidden sm:inline">
                                                        {countries.find(c => c.name === selectedCountry)?.flag}
                                                    </span>
                                                </span>
                                            )
                                            : (
                                                <span className="flex items-center flex-wrap">
                                                    No numbers found in {selectedCountry}
                                                    <span className="ml-2 hidden sm:inline">
                                                        {countries.find(c => c.name === selectedCountry)?.flag}
                                                    </span>
                                                </span>
                                            )
                                        }
                                    </p>
                                </div>

                                {/* Results Grid */}
                                {searchResults.length > 0 ? (
                                    <div className="grid gap-6 grid-cols-1">
                                        {searchResults.map((option, index) => (
                                            <div
                                                key={option.id}
                                                className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-600/50 border-blue-500/50 transition-all duration-300 shadow-lg shadow-blue-500/25 hover:scale-[1.01]" style={{ boxShadow: '0 0 24px rgba(59, 130, 246, 0.25)' }}
                                            >
                                                {/* Number Header */}
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-9 h-9 bg-blue-300/10 rounded-xl flex items-center justify-center">
                                                            <span className="text-emerald-400 font-bold text-sm">{['1st', '2nd', '3rd'][index] || `${index + 1}th`}</span>
                                                        </div>
                                                        <div>
                                                            <p className="text-white font-bold text-md">Option</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Details in one line */}
                                                <div className="md:flex md:items-center md:justify-between">
                                                    {/* Mobile Layout */}
                                                    <div className="md:hidden space-y-3">
                                                        {/* Price, Duration, Renewable in two columns */}
                                                        <div className="space-y-2">
                                                            <div className="flex items-center justify-between text-md">
                                                                <span className="text-slate-300 font-medium">Price:</span>
                                                                <span className="text-emerald-400 font-semibold">
                                                                    ${formatPrice(option.price)}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-md">
                                                                <span className="text-slate-300 font-medium">Duration:</span>
                                                                <span className="text-emerald-400 font-semibold">
                                                                    {option.duration} days
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-md">
                                                                <span className="text-slate-300 font-medium">Renewable:</span>
                                                                <span className="text-emerald-400 font-semibold">Yes</span>
                                                            </div>
                                                        </div>
                                                        {/* Purchase Button - full width */}
                                                        <button
                                                            onClick={() => handlePurchase(option)}
                                                            disabled={purchasingOptionId !== null}
                                                            className="w-full px-6 py-2 bg-gradient-to-r from-green-400 to-blue-500 hover:from-green-500 hover:to-blue-600 text-white font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-blue-500/25 hover:scale-[1.02] text-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                                        >
                                                            {purchasingOptionId === option.id ? (
                                                                <div className="flex items-center justify-center">
                                                                    <svg className="w-4 h-4 animate-spin mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                    </svg>
                                                                </div>
                                                            ) : (
                                                                'Purchase'
                                                            )}
                                                        </button>
                                                    </div>

                                                    {/* Desktop Layout - unchanged */}
                                                    <div className="hidden md:flex md:items-center md:space-x-2 text-md">
                                                        <span className="text-slate-300 font-medium">Price:</span>
                                                        <span className="text-emerald-400 font-semibold">
                                                            ${formatPrice(option.price)}
                                                        </span>
                                                    </div>

                                                    <div className="hidden md:flex md:items-center md:space-x-2 text-md">
                                                        <span className="text-slate-300 font-medium">Duration:</span>
                                                        <span className="text-emerald-400 font-semibold">
                                                            {option.duration} days
                                                        </span>
                                                    </div>

                                                    <div className="hidden md:flex md:items-center md:space-x-2 text-md">
                                                        <span className="text-slate-300 font-medium">Renewable:</span>
                                                        <span className="text-emerald-400 font-semibold">Yes</span>
                                                    </div>

                                                    <button
                                                        onClick={() => handlePurchase(option)}
                                                        disabled={purchasingOptionId !== null}
                                                        className="hidden md:block px-6 py-2 bg-gradient-to-r from-green-400 to-blue-500 hover:from-green-500 hover:to-blue-600 text-white font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-blue-500/25 hover:scale-[1.02] text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 min-w-[120px]"
                                                    >
                                                        {purchasingOptionId === option.id ? (
                                                            <div className="flex items-center justify-center">
                                                                <svg className="w-4 h-4 animate-spin mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                </svg>
                                                            </div>
                                                        ) : (
                                                            'Purchase'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-16 max-w-md mx-auto">
                                        <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-700 rounded-2xl mb-6">
                                            <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.291-1.007-5.824-2.562M15 6.306a7.962 7.962 0 00-6 0m6 0C17.742 7.324 20.467 9.45 21 12.017M9 6.306C6.258 7.324 3.533 9.45 3 12.017" />
                                            </svg>
                                        </div>
                                        <h4 className="text-2xl font-bold text-slate-300 mb-3">No Numbers Available</h4>
                                        <p className="text-slate-400 text-lg">Try searching for a different service or country.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Error Modal */}
            {showErrorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ margin: '0' }}>
                    <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-6 w-80">
                        <div className="text-center">
                            <div className="mb-4">
                                <div className="w-12 h-12 mx-auto bg-red-500 rounded-full flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-lg font-medium text-white mb-2">Error</h3>
                            <p className="text-blue-200 mb-4">{errorMessage}</p>
                            <button
                                onClick={handleErrorModalClose}
                                className="bg-gradient-to-r from-green-400 to-blue-500 hover:from-green-500 hover:to-blue-600 text-white font-medium py-2 px-4 rounded-xl transition-all duration-300 shadow-lg"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmptySimcard;